import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Save } from 'lucide-react';

interface AutoSaveProps {
  onSave: () => Promise<void> | void;
  delay?: number;
  className?: string;
}

const AutoSave: React.FC<AutoSaveProps> = ({ onSave, delay = 2000, className = '' }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!hasChanges) return;

    const timer = setTimeout(async () => {
      setIsSaving(true);
      try {
        await onSave();
        setIsSaved(true);
        setHasChanges(false);
        
        // Reset saved state after 2 seconds
        setTimeout(() => setIsSaved(false), 2000);
      } catch (error) {
        console.error('Auto-save failed:', error);
      } finally {
        setIsSaving(false);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [hasChanges, onSave, delay]);

  // This function should be called when form data changes
  const triggerSave = () => {
    setHasChanges(true);
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <AnimatePresence mode="wait">
        {isSaving && (
          <motion.div
            key="saving"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="flex items-center space-x-2 text-blue-600"
          >
            <Save className="w-4 h-4 animate-pulse" />
            <span className="text-sm">Sauvegarde...</span>
          </motion.div>
        )}
        
        {isSaved && (
          <motion.div
            key="saved"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="flex items-center space-x-2 text-green-600"
          >
            <Check className="w-4 h-4" />
            <span className="text-sm">Sauvegardé</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AutoSave;

